/**
 * What a dashboard widget reads — ONE description for every reader (finance-e2e 03 U2): the page
 * (`client/src/pages/dashboards/WidgetCard.tsx` builds its request from it) and `dashboard_read { data: true }` (the
 * dashboard skill reads the same data for an external client). A widget's effective settings are its catalog defaults
 * with its own settings over them.
 *   - `dataSource: 'output'` — the newest dashboard-visible output parts (10), of one `kind` when set, of one workflow
 *     when `workflowId` is set, else the organization's feed; only those titled `partTitle` when set (a workflow that
 *     writes several tables binds each widget to its own, TA desk U9);
 *   - `dataSource: 'metric'` — one workflow's metric series by `metricName`, over `timeRange` (default 30 days), at
 *     most `limit` points (default 500, at most 5000).
 * Anything else reads nothing.
 */

export const WIDGET_OUTPUT_LIMIT = 10;
export const DEFAULT_WIDGET_TIME_RANGE_MS = 30 * 86_400_000;
const DEFAULT_METRIC_LIMIT = 500;
const MAX_METRIC_LIMIT = 5000;

const UNIT_MS: Record<string, number> = {
  h: 3_600_000,
  d: 86_400_000,
  w: 7 * 86_400_000,
  m: 30 * 86_400_000,
  y: 365 * 86_400_000,
};

/**
 * A `timeRange` like "30d", "90d", "24h", "12w" as milliseconds. Anything unrecognised falls back to 30 days rather
 * than throwing or querying everything — a tile showing the wrong window is bad, one that drags the whole series back
 * is worse.
 */
export function widgetTimeRangeMs(value: unknown): number {
  if (typeof value !== 'string') return DEFAULT_WIDGET_TIME_RANGE_MS;
  const match = /^(\d+)\s*([hdwmy])$/i.exec(value.trim());
  if (!match) return DEFAULT_WIDGET_TIME_RANGE_MS;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount <= 0) return DEFAULT_WIDGET_TIME_RANGE_MS;
  return amount * (UNIT_MS[(match[2] ?? 'd').toLowerCase()] ?? 86_400_000);
}

export type WidgetDataPlan =
  | { source: 'output'; kind: string; workflowId: string | null; partTitle: string | null; limit: number }
  | { source: 'metric'; workflowId: string; metricName: string; rangeMs: number; limit: number };

export function effectiveWidgetSettings(
  defaults: Record<string, unknown> | null | undefined,
  own: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return { ...(defaults ?? {}), ...(own ?? {}) };
}

/** What a widget with these effective settings reads, or null when it reads nothing. */
export function widgetDataPlan(settings: Record<string, unknown>): WidgetDataPlan | null {
  const raw = settings.workflowId;
  const workflowId = (typeof raw === 'number' || typeof raw === 'string') && raw ? String(raw) : null;
  if (settings.dataSource === 'output') {
    const kind = typeof settings.kind === 'string' ? settings.kind : '';
    const partTitle =
      typeof settings.partTitle === 'string' && settings.partTitle.trim() ? settings.partTitle.trim() : null;
    return { source: 'output', kind, workflowId, partTitle, limit: WIDGET_OUTPUT_LIMIT };
  }
  if (settings.dataSource === 'metric') {
    const metricName = typeof settings.metricName === 'string' ? settings.metricName : '';
    if (!workflowId || !metricName) return null;
    const asked = Number(settings.limit);
    return {
      source: 'metric',
      workflowId,
      metricName,
      rangeMs: widgetTimeRangeMs(settings.timeRange),
      limit: asked > 0 ? Math.min(asked, MAX_METRIC_LIMIT) : DEFAULT_METRIC_LIMIT,
    };
  }
  return null;
}
